
import { computed, defineComponent, nextTick, ref } from 'vue';
import VRuntimeTemplate from 'vue3-runtime-template';

import { ComponentName } from '@/catalogs/api/configuration/components/ComponentName';
import { DataType } from '@/catalogs/api/configuration/application/DataType';
import { setupComponent } from '@/catalogs/composables/setupComponent';
import { DetailsCommand, PreviewCommand } from '@/catalogs/api/runtime/CommandExecutor';
import { attrValue, attrValues, additionalInfo } from '@/common/helpers/productAttributes';

import { IItemsListOptions } from '@/catalogs/api/configuration/components/IItemsListOptions';

import InjectStyles from '@/common/components/InjectStyles.vue';
import Icon from '@/common/components/Icon.vue';
import Clickable from '@/common/components/Clickable.vue';
import ItemCatalogsDetailsModal from './components/ItemCatalogsDetailsModal.vue';
import {
  CatalogProductTypeEnum,
  CatalogProductItem,
  ProductItem,
} from '@/common/services/swagger/index.defs';
import Image from '@/common/components/Image.vue';
export default defineComponent({
  name: ComponentName.searchProductsList,

  components: {
    InjectStyles,
    Icon,
    VRuntimeTemplate,
    Clickable,
    Image,
    ItemCatalogsDetailsModal,
  },

  props: {
    instanceId: String,
  },

  setup(props) {
    const root = ref<HTMLElement>();
    const catalogProductType = CatalogProductTypeEnum;

    const {
      componentName,
      isReady,
      isWebComponent,
      instance,
      store,
      routeData,
      t,
      widerThan,
      narrowerThan,
      emit,
    } = setupComponent(root, props.instanceId, [DataType.Products]);

    const componentOptions = computed(
      () => store.value?.options.components?.itemsList ?? ({} as IItemsListOptions),
    );

    const currentCIDName = computed(() => {
      const cid = instance.value?.router.routeData?.products.cid;
      if (cid) {
        const breadcrumbs = instance.value?.store?.data.breadcrumbs?.items;
        if (breadcrumbs) {
          return breadcrumbs[breadcrumbs.length - 1]?.name;
        }
      }
      return undefined;
    });

    const details = async (product: CatalogProductItem): Promise<void> => {
      if (product.inactive) {
        return;
      }
      try {
        if (routeData.value && product) {
          await instance.value?.execute(new DetailsCommand(product as ProductItem));
        }
      } catch (error) {
        //Ignore Error
      }
    };

    const baseProductDetails = async (baseProduct: ProductItem | undefined): Promise<void> => {
      if (!baseProduct) return;
      if (baseProduct.inactive) {
        return;
      }
      try {
        await instance.value?.execute(new DetailsCommand(baseProduct));
      } catch (error) {
        //Ignore Error
      }
    };

    const productsItems = computed(() => store.value?.data.products?.items);

    const previewEnabled = computed(
      () => instance.value?.registeredComponents.has(ComponentName.preview) ?? false,
    );

    const isPreviewDisabled = (item: CatalogProductItem) =>
      (item.baseProduct && item.baseProduct.inactive) || item.inactive;

    const previewByProductId = async (productId: string): Promise<void> => {
      try {
        await instance.value?.execute(new PreviewCommand({ productId }));
      } catch (error) {
        // Ignore Error
      }
    };

    const selectedItem = ref();
    const catalogsModalVisible = ref(false);

    const toggleItemDetails = (product: CatalogProductItem) => {
      selectedItem.value = product;
      catalogsModalVisible.value = false;
      nextTick(() => {
        catalogsModalVisible.value = true;
      });
    };

    const getItemImage = (item: CatalogProductItem): string | undefined => {
      if (item.baseProduct) {
        return (item.baseProduct.images || [])[0]?.url;
      }
      return (item.images || [])[0]?.url;
    };

    const showingAllCatalogs = computed(() => {
      return !routeData.value?.products.catalogId;
    });

    const showActionTemplate = (item: CatalogProductItem) => {
      return (
        componentOptions.value.actionTemplate &&
        (componentOptions.value.showActionTemplateIfInactive || !item?.inactive)
      );
    };

    return {
      root,
      instance,
      componentName,
      componentOptions,
      isReady,
      isWebComponent,
      store,
      selectedItem,
      productsItems,
      previewEnabled,
      currentCIDName,
      showingAllCatalogs,
      catalogProductType,
      catalogsModalVisible,
      CatalogProductTypeEnum,
      t,
      widerThan,
      narrowerThan,
      attrValue,
      attrValues,
      details,
      additionalInfo,
      emit,
      getItemImage,
      isPreviewDisabled,
      toggleItemDetails,
      previewByProductId,
      baseProductDetails,
      showActionTemplate,
    };
  },
});
